import { Tooltip } from 'portal-commons';
import { FunctionComponent, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { faArrowProgress } from '@fortawesome/pro-regular-svg-icons';
import { Grid } from '@material-ui/core';

import HeadingBlock from './HeadingBlock';
import { CampaignDetailsFormValues, Reseller } from '../../types';
import { createRequiredValidator } from '../../validators';
import { IntegrationReactSelect } from '@/ui_elements';
import { Cnp, SelectItem } from '@/utils/types';

interface OtherResponsiblePartiesSectionProp {
  cnps?: Cnp[];
  resellers?: Reseller[];
  onNewResellerRequest?: () => void;
}

const OtherResponsiblePartiesSection: FunctionComponent<
  OtherResponsiblePartiesSectionProp
> = ({ cnps = [], resellers = [], onNewResellerRequest }) => {
  const { control } = useFormContext<CampaignDetailsFormValues>();
  const {
    field: { value: upstreamCnpUid, onChange: setUpstreamCnpUid },
    fieldState: { error: upstreamCnpUidError },
  } = useController({
    control,
    name: 'upstreamCnpUid',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please select your Connectivity Partner'
        ),
      },
    },
  });
  const {
    field: { value: resellerUid, onChange: setResellerUid },
    fieldState: { error: resellerUidError },
  } = useController({
    control,
    name: 'resellerUid',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a Reseller'),
      },
    },
  });
  const cnpOptions = useMemo<SelectItem[]>(
    () =>
      cnps.map((cnp) => ({
        label: cnp.displayName,
        value: cnp.uid,
      })),
    [cnps]
  );
  const resellerOptions = useMemo<SelectItem[]>(
    () => [
      { label: 'No Reseller', value: 'R000000' },
      { label: 'Add New Reseller', value: 'new' },
      ...resellers.map((reseller) => ({
        label: reseller.companyName,
        value: reseller.uid,
      })),
    ],
    [resellers]
  );

  return (
    <Grid item xs={12} data-testid="campaignDetailsFormOtherResponsibleParties">
      <div className="campaign-dca wrapper">
        <HeadingBlock
          icon={faArrowProgress}
          title="Other Responsible Parties"
        />
        <Grid container>
          <Grid item xs={8}>
            <div className="description-block">
              For Sole Proprietor campaigns, if your CNP is not showing in the
              list, it means they are not enabled to receive Sole Proprietor
              campaigns. Please reach out to your CNP for more details.
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <div
              className="form-group-field"
              data-testid="campaignDetailsFormOtherResponsiblePartiesSelect"
            >
              <IntegrationReactSelect
                options={cnpOptions}
                label="Select your Connectivity Partner"
                required={true}
                error={!!upstreamCnpUidError?.message}
                value={cnpOptions.find(
                  (option) => option.value === upstreamCnpUid
                )}
                handleChange={(option: SelectItem) =>
                  setUpstreamCnpUid(option.value)
                }
                keyName="upstreamCnpUid"
              />
              <Tooltip
                title="The “Connectivity Partner” is the Campaign Service Provider who you send messages to."
                placement="right-end"
              />
              {upstreamCnpUidError?.message && (
                <h6 className="error-msg">{upstreamCnpUidError.message}</h6>
              )}
            </div>
          </Grid>
          <Grid item xs={6}>
            <div
              className="form-group-field"
              data-testid="campaignDetailsFormOtherResponsiblePartiesResellerSelect"
            >
              <IntegrationReactSelect
                options={resellerOptions}
                label="Select reseller"
                required={true}
                error={!!resellerUidError?.message}
                value={resellerOptions.find(
                  (option) => option.value === resellerUid
                )}
                handleChange={(option: SelectItem) => {
                  if (option.value === 'new') {
                    onNewResellerRequest?.();
                  } else {
                    setResellerUid(option.value);
                  }
                }}
                keyName="resellerUid"
              />
              <Tooltip title="Select this option if you are registering a Campaign on behalf of a reseller or service provider." />
              {resellerUidError?.message && (
                <h6 className="error-msg">{resellerUidError.message}</h6>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};

export default OtherResponsiblePartiesSection;
